// A run you can watch without any infrastructure.
//
// A real follow needs a peer to reach: a driver's phone publishing PMT1
// records that this device can find through the rendezvous key. In
// development there is no such phone, so this module makes one — an
// in-tab publisher driving a plan over the loopback transport, exactly as
// rangefind's own demo does.
//
// **It is the same code path, not a parallel implementation.** The
// publisher is `createThreadPublisher` through `channel.publish`, the
// records are real PMT1 bytes, sealed and signed; they cross a MeshNode,
// get validated by the subscriber's nine steps, and the arrival time is
// computed by `estimateArrival` from a position the follower decrypted.
// The only thing that is not real is the vehicle, which is a point
// walking down a polyline the router itself produced.
//
// A publisher and a follower on one peer cannot see each other — no
// transport loops a publish back to its own sender, in gossipsub or
// anywhere else — so the publisher gets its own mesh node beside the
// viewer's, which is what a phone across town would be.

import type { EngineBundles, LatLon, MeshSession, RouteEngine, ThreadRun } from "./engine.js";
import { STOP_OUTCOME, STOP_REASON, THREAD_MODE, type RunPlan } from "./protocol.js";
import type { WayfindSession } from "./session.js";

/** One scripted stop: where it is, what it is called, and what happens there. */
export interface DemoStop {
  label: string;
  lat: number;
  lon: number;
  /** What the driver asserts on arrival. Pending means they drive on. */
  outcome?: number;
  reason?: number;
  /** Attached to the mark, so the "photo exists" chip has something to say. */
  photo?: boolean;
}

export interface DemoScenario {
  id: string;
  title: string;
  /** One line the app can print next to the demo banner. */
  blurb: string;
  granularity: "fine" | "coarse";
  /** 1 car, 2 bike, 3 foot — §5.2 field 5. */
  travelMode: number;
  /** Which stop the person holding this link is waiting at. */
  myStopIndex: number;
  stops: DemoStop[];
  /** How many seconds the whole round should take on screen. */
  runSeconds: number;
  /** Seconds the van stands at each stop. */
  dwellSeconds: number;
}

export interface DemoRun {
  /** The capability URL, fragment and all — exactly what an SMS would carry. */
  url: string;
  link: Uint8Array;
  plan: RunPlan;
  myStopIndex: number;
  scenario: DemoScenario;
  /** Mark this run finished, as a driver closing the day would. */
  finish(): Promise<void>;
  stop(): Promise<void>;
}

/**
 * Luxembourg City, because that is the region the development route graph
 * covers. Coordinates are on the graph — a stop the router cannot snap is
 * a demo that produces no estimate.
 */
export const DEMO_SCENARIOS: readonly DemoScenario[] = Object.freeze([
  {
    id: "delivery",
    title: "A courier round",
    blurb: "Five drops, live position, and your parcel is the fourth.",
    granularity: "fine",
    travelMode: 1,
    myStopIndex: 4,
    runSeconds: 260,
    dwellSeconds: 8,
    stops: [
      { label: "Gare de Sainte-Thérèse", lat: 45.6383, lon: -73.8497, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Boul. du Curé-Labelle", lat: 45.6420, lon: -73.8460, outcome: STOP_OUTCOME.DELIVERED, photo: true },
      { label: "Chemin de la Grande-Côte", lat: 45.6180, lon: -73.8360, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Place Rosemère", lat: 45.6318, lon: -73.8080, outcome: STOP_OUTCOME.DELIVERED, photo: true },
      { label: "Rue Armstrong", lat: 45.6260, lon: -73.7995, outcome: STOP_OUTCOME.DELIVERED }
    ]
  },
  {
    id: "skipped",
    title: "A round where yours is skipped",
    blurb: "The driver could not get to your door, and says so before the round ends.",
    granularity: "fine",
    travelMode: 1,
    myStopIndex: 3,
    runSeconds: 240,
    dwellSeconds: 8,
    stops: [
      { label: "Gare de Sainte-Thérèse", lat: 45.6383, lon: -73.8497, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Boul. du Curé-Labelle", lat: 45.6420, lon: -73.8460, outcome: STOP_OUTCOME.DELIVERED },
      {
        label: "Chemin de la Grande-Côte",
        lat: 45.6180,
        lon: -73.8360,
        outcome: STOP_OUTCOME.SKIPPED,
        reason: STOP_REASON.INACCESSIBLE
      },
      { label: "Place Rosemère", lat: 45.6318, lon: -73.8080, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Rue Armstrong", lat: 45.6260, lon: -73.7995, outcome: STOP_OUTCOME.DELIVERED }
    ]
  },
  {
    id: "school",
    title: "A school run",
    blurb: "Stops only. No position is published, and none is drawn.",
    granularity: "coarse",
    travelMode: 1,
    myStopIndex: 4,
    runSeconds: 280,
    dwellSeconds: 12,
    stops: [
      { label: "Stop 1 — Gare", lat: 45.6383, lon: -73.8497, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Stop 2 — Boul. du Curé-Labelle", lat: 45.6420, lon: -73.8460, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Stop 3 — Chemin de la Grande-Côte", lat: 45.6180, lon: -73.8360, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Stop 4 — Place Rosemère", lat: 45.6318, lon: -73.8080, outcome: STOP_OUTCOME.DELIVERED },
      { label: "Stop 5 — Rue Armstrong", lat: 45.6260, lon: -73.7995, outcome: STOP_OUTCOME.DELIVERED }
    ]
  }
]);

export function scenarioById(id: string): DemoScenario {
  return DEMO_SCENARIOS.find(scenario => scenario.id === id) ?? DEMO_SCENARIOS[0]!;
}

const TICK_MS = 1000;
const EARTH_RADIUS_M = 6371000;

function metersBetween(a: LatLon, b: LatLon): number {
  const toRad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toRad;
  const dLon = (b.lon - a.lon) * toRad;
  const lat = ((a.lat + b.lat) / 2) * toRad;
  const x = dLon * Math.cos(lat);
  return Math.hypot(dLat, x) * EARTH_RADIUS_M;
}

interface PathPoint extends LatLon {
  /** Cumulative metres from the start of the whole round. */
  at: number;
  /** The plan stop this point *is*, when it is one. */
  stopIndex: number | null;
}

/**
 * The road the van will actually drive, from the router.
 *
 * Legs are routed one pair at a time in plan order — never `itinerary()`,
 * which reorders stops (§9). A demo that shortcut the plan would show an
 * arrival the real run could not meet.
 */
async function buildPath(engine: RouteEngine, stops: DemoStop[]): Promise<PathPoint[]> {
  const path: PathPoint[] = [];
  let cumulative = 0;
  let previous: LatLon | null = null;
  for (let i = 0; i + 1 < stops.length; i++) {
    const from = stops[i]!;
    const to = stops[i + 1]!;
    const leg = await engine.route({ from, to, geometry: true });
    const geometry = leg.geometry ?? [[from.lat, from.lon], [to.lat, to.lon]];
    for (let j = 0; j < geometry.length; j++) {
      const [lat, lon] = geometry[j]!;
      const point = { lat, lon };
      if (previous) cumulative += metersBetween(previous, point);
      previous = point;
      const isFirst = j === 0;
      const isLast = j === geometry.length - 1;
      if (isFirst && i > 0) continue; // the previous leg already ended here
      path.push({
        ...point,
        at: cumulative,
        stopIndex: isFirst ? i + 1 : (isLast ? i + 2 : null)
      });
    }
  }
  return path;
}

function pointAt(path: PathPoint[], meters: number): { point: LatLon; index: number } {
  if (!path.length) return { point: { lat: 0, lon: 0 }, index: 0 };
  let index = 0;
  while (index + 1 < path.length && path[index + 1]!.at <= meters) index++;
  const current = path[index]!;
  const next = path[index + 1];
  if (!next) return { point: { lat: current.lat, lon: current.lon }, index };
  const span = next.at - current.at;
  const ratio = span > 0 ? (meters - current.at) / span : 0;
  return {
    point: {
      lat: current.lat + (next.lat - current.lat) * ratio,
      lon: current.lon + (next.lon - current.lon) * ratio
    },
    index
  };
}

/**
 * Starts a demo run and returns its capability link.
 *
 * The publisher runs on the session's demo bus — a loopback network of
 * this tab's own — as a separate mesh node from the subscriber there,
 * because a peer never receives its own publish. Everything from that
 * point is the real path: sealed records, the subscriber's validation
 * pipeline, and a locally computed ETA.
 */
export async function startDemoRun(
  session: WayfindSession,
  scenario: DemoScenario,
  options: { baseUrl?: string } = {}
): Promise<DemoRun> {
  const engine = session.engine;
  if (!engine) {
    throw new Error(
      "A demo run needs a routing map: the thread's topic tag is derived from the graph's epoch "
      + "(threads §4.2), so without a graph there is nothing to publish onto."
    );
  }
  const bundles: EngineBundles = session.bundles;
  // The demonstration's own loopback bus, never the page's network.
  //
  // Sharing the page's was correct while the only transport was
  // loopback. Once a keeper is configured — which is every deployment
  // now — the page's network is libp2p, a libp2p host takes exactly one
  // MeshNode, and the viewer's already holds it: the second one below
  // threw "One MeshNode per libp2p host." and the front page's demos
  // stopped working for everybody.
  const bus = await session.demoBus();
  if (!bus) {
    throw new Error(
      "A demo run needs a routing map: the thread's topic tag is derived from the graph's epoch "
      + "(threads §4.2), so without a graph there is nothing to publish onto."
    );
  }
  const network = bus.network;

  const publisherMesh: MeshSession = await bundles.pulsemesh.createMeshSession({
    engine,
    network,
    // The epoch the page rendezvouses on, not the graph's content hash.
    // The subscriber on this bus derives its topic from the session's,
    // so a publisher that fell back to the hash would publish onto a tag
    // nobody in the tab is listening to — the silent failure the dataset
    // epoch exists to prevent, reproduced in miniature.
    epochHex: session.epochHex,
    id: "demo-van",
    transport: "loopback",
    contribute: false
  });
  publisherMesh.start();

  // A coarse run publishes stop events plus a heartbeat. Sixty seconds is
  // the protocol default and correct for a real bus; a person looking at a
  // demonstration should not stare at an empty screen for a minute, so the
  // heartbeat alone is shortened. Nothing else is touched — staleness, max
  // age and the record format are the shipping constants.
  //
  // These are the **thread** constants, and that matters: handing the
  // traffic channel's `DEFAULT_CONSTANTS` to a thread publisher leaves
  // `THREAD_UPDATE_FINE` undefined, the cadence comparison becomes NaN,
  // and the run publishes its first record and then goes silent forever.
  const constants = {
    ...(bundles.threads.THREAD_CONSTANTS as Record<string, unknown>),
    THREAD_COARSE_HEARTBEAT: 6
  };

  const channel = bundles.threads.createThreadChannel({
    node: (publisherMesh as unknown as { node: unknown }).node,
    network,
    engine,
    engineMode: engine.root.profile ?? "car",
    id: "demo-van",
    constants
  });

  const plan: RunPlan = {
    stops: scenario.stops.map((stop, index) => ({
      index: index + 1,
      lat: stop.lat,
      lon: stop.lon,
      label: stop.label
    })),
    dwellSeconds: scenario.dwellSeconds
  };

  const run: ThreadRun = await channel.publish({
    baseUrl: options.baseUrl ?? `${location.origin}${location.pathname}`,
    mode: scenario.granularity === "fine" ? THREAD_MODE.FINE : THREAD_MODE.COARSE,
    plan,
    travelMode: scenario.travelMode,
    ttlSeconds: 3 * 3600
  });

  const path = await buildPath(engine, scenario.stops);
  const totalMeters = path.length ? path[path.length - 1]!.at : 0;
  const metersPerSecond = totalMeters > 0 ? totalMeters / scenario.runSeconds : 0;
  /**
   * Where each stop sits along the round, in metres.
   *
   * Arrival is decided by *passing* this distance, not by being within
   * some radius of the stop when a tick happens to land. A proximity test
   * quietly depends on the tick interval being short relative to the
   * speed: run the demonstration faster and the van teleports over its own
   * capture radius, every stop stays pending, and the screen looks
   * plausible while being wrong.
   */
  const stopMeters = new Map<number, number>();
  for (const point of path) {
    if (point.stopIndex != null && !stopMeters.has(point.stopIndex)) {
      stopMeters.set(point.stopIndex, point.at);
    }
  }

  let meters = 0;
  let dwellUntil = 0;
  let dealtWith = 0;
  let finished = false;
  let lastTickMillis = Date.now();

  async function tick(): Promise<void> {
    if (finished) return;
    const now = Date.now();
    // Progress comes from the **wall clock**, not from counting ticks. A
    // browser throttles timers in a background tab to roughly one a
    // minute, and a van whose position is `meters += metersPerSecond` per
    // tick would crawl whenever the demonstration is not on screen — which
    // is exactly when someone is reading the code and wondering why it
    // looks broken.
    const elapsed = Math.max(0, now - lastTickMillis) / 1000;
    lastTickMillis = now;
    const dwelling = now < dwellUntil;

    // Arriving at the next stop: pull up exactly on it, stand still, and
    // then say what happened. Dwell detection moves `stopIndex` on its
    // own; the mark is what writes the outcome, because an outcome is
    // asserted and never inferred (§5.2.1).
    let arriving: number | null = null;
    if (!dwelling) {
      const next = dealtWith + 1;
      const nextAt = stopMeters.get(next);
      const advanced = Math.min(totalMeters, meters + metersPerSecond * elapsed);
      if (nextAt != null && advanced >= nextAt) {
        meters = nextAt;
        arriving = next;
      } else {
        meters = advanced;
      }
    }

    const { point } = pointAt(path, meters);
    await run.handleFix({
      lat: point.lat,
      lon: point.lon,
      speedMps: dwelling || arriving != null ? 0 : metersPerSecond
    }).catch(() => null);

    if (arriving != null) {
      dwellUntil = now + scenario.dwellSeconds * 1000;
      dealtWith = arriving;
      const stop = scenario.stops[arriving - 1]!;
      const outcome = stop.outcome ?? STOP_OUTCOME.PENDING;
      if (outcome !== STOP_OUTCOME.PENDING) {
        await run.markStop(arriving, outcome, {
          reason: stop.reason ?? STOP_REASON.NONE,
          // A photo commitment is a hash of sealed bytes; the customer
          // cannot open it, and the app says so rather than pretending.
          photo: stop.photo && scenario.granularity === "fine"
            ? demoPhotoBytes(arriving)
            : null
        }).catch(() => null);
      }
    }

    await channel.tick().catch(() => null);
    if (meters >= totalMeters && dealtWith >= scenario.stops.length && !dwelling) {
      finished = true;
      await run.finish().catch(() => null);
    }
  }

  const timer = setInterval(() => { void tick(); }, TICK_MS);
  await tick();
  const url = run.url ?? "";
  return {
    url,
    link: run.link,
    plan,
    myStopIndex: scenario.myStopIndex,
    scenario,
    async finish() {
      finished = true;
      await run.finish().catch(() => null);
    },
    async stop() {
      finished = true;
      clearInterval(timer);
      await run.finish().catch(() => null);
      channel.close();
      await publisherMesh.close().catch(() => null);
    }
  };
}

/**
 * Bytes for a proof photo. Deliberately not an image: a demo must not
 * teach a host to hand `markStop` a camera file. §20.7 is a MUST — an
 * unprocessed camera JPEG carries EXIF, and EXIF carries a GPS fix of a
 * customer's front door, past every granularity control in §11. A real
 * host re-encodes through a canvas first.
 */
function demoPhotoBytes(stopIndex: number): Uint8Array {
  const bytes = new Uint8Array(512);
  for (let i = 0; i < bytes.length; i++) bytes[i] = (i * 31 + stopIndex * 7) & 0xff;
  return bytes;
}
